import React, { useState, useEffect } from 'react';
import { ProjectRow } from '../../lib/types';
import styles from './ideas-public.module.css';

interface IdeaWithVotes extends ProjectRow {
  voteCount: number;
  commentCount: number;
  userVote?: 'up' | 'down' | null;
}

export default function PublicIdeaList() {
  const [ideas, setIdeas] = useState<IdeaWithVotes[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<'votes' | 'date' | 'domain'>('votes');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDomain, setSelectedDomain] = useState<string>('');
  const [userEmail, setUserEmail] = useState<string>('');

  useEffect(() => {
    fetchIdeas();
    // Get user email from localStorage if exists
    const email = localStorage.getItem('auth_email') || 'anonymous';
    setUserEmail(email);
  }, []);

  const fetchIdeas = async () => {
    try {
      setLoading(true);
      const response = await fetch('/projects');
      const data = await response.json();

      if (data.success) {
        // Filter to discovery phase ideas (only Gathering Feedback and Ready for Governance, not NewIdea)
        const discoveryProjects = (data.data || [])
          .filter((p: ProjectRow) => ['GatheringFeedback', 'ReadyForGovernance'].includes(p.Status));

        // Fetch vote and comment counts for each idea
        const ideasWithCounts = await Promise.all(
          discoveryProjects.map(async (p: ProjectRow) => {
            try {
              const votesRes = await fetch(`/projects/${p.ProjectId}/votes`);
              const votesData = await votesRes.json();
              const voteCount = (votesData.data?.UpVotes || 0) + (votesData.data?.DownVotes || 0);

              const commentsRes = await fetch(`/projects/${p.ProjectId}/comments`);
              const commentsData = await commentsRes.json();
              const commentCount = (commentsData.data || []).length;

              return {
                ...p,
                voteCount,
                commentCount,
              };
            } catch (error) {
              console.error(`Failed to fetch counts for ${p.ProjectId}:`, error);
              return {
                ...p,
                voteCount: 0,
                commentCount: 0,
              };
            }
          })
        );

        setIdeas(ideasWithCounts);
      }
    } catch (error) {
      console.error('Failed to fetch ideas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (projectId: string, voteType: 'up' | 'down') => {
    try {
      const response = await fetch(`/projects/${projectId}/votes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          voteType,
          voterEmail: userEmail,
          voterName: userEmail.split('@')[0],
        }),
      });

      if (response.ok) {
        const voteData = await response.json();
        // Update local state with actual vote counts from server
        setIdeas(ideas.map(idea =>
          idea.ProjectId === projectId
            ? {
                ...idea,
                voteCount: (voteData.data?.UpVotes || 0) + (voteData.data?.DownVotes || 0),
                userVote: voteType,
              }
            : idea
        ));
      }
    } catch (error) {
      console.error('Failed to submit vote:', error);
    }
  };

  // Filter ideas
  let filteredIdeas = ideas.filter(idea => {
    const matchesSearch = idea.Title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         idea.Description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDomain = !selectedDomain || idea.Domain === selectedDomain;
    return matchesSearch && matchesDomain;
  });

  // Sort ideas
  filteredIdeas = [...filteredIdeas].sort((a, b) => {
    if (sortBy === 'votes') return b.voteCount - a.voteCount;
    if (sortBy === 'date') return new Date(b.CreatedDate).getTime() - new Date(a.CreatedDate).getTime();
    return a.Domain.localeCompare(b.Domain);
  });

  if (loading) {
    return <div className={styles.loading}>Loading ideas...</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1>Ideas</h1>
            <p className={styles.subtitle}>
              See all ideas, vote on your favorites, and share your thoughts
            </p>
          </div>
          <a
            href="#/submit-idea"
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '500',
              textDecoration: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            + Add Idea
          </a>
        </div>
      </div>

      <div className={styles.controls}>
        <input
          type="text"
          placeholder="Search ideas..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={styles.searchInput}
        />

        <select
          value={selectedDomain}
          onChange={(e) => setSelectedDomain(e.target.value)}
          className={styles.filterSelect}
        >
          <option value="">All Domains</option>
          <option value="infrastructure">Infrastructure</option>
          <option value="networking">Networking</option>
          <option value="voice">Voice</option>
          <option value="product">Product</option>
          <option value="development">Development</option>
        </select>

        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as 'votes' | 'date' | 'domain')}
          className={styles.sortSelect}
        >
          <option value="votes">Sort by Votes</option>
          <option value="date">Sort by Date</option>
          <option value="domain">Sort by Domain</option>
        </select>
      </div>

      {filteredIdeas.length === 0 ? (
        <div className={styles.emptyState}>
          <p>No ideas match your search</p>
        </div>
      ) : (
        <div className={styles.ideaList}>
          {filteredIdeas.map(idea => (
            <div key={idea.ProjectId} className={styles.ideaCard}>
              <div className={styles.ideaHeader}>
                <h3 className={styles.ideaTitle}>{idea.Title}</h3>
                <span className={styles.domain}>{idea.Domain}</span>
              </div>

              <p className={styles.ideaDescription}>{idea.Description}</p>

              <div className={styles.ideaFooter}>
                <div className={styles.meta}>
                  <span className={styles.submitter}>
                    by {idea.CreatedByName}
                  </span>
                  <span className={styles.date}>
                    {new Date(idea.CreatedDate).toLocaleDateString()}
                  </span>
                </div>

                <div className={styles.actions}>
                  <button
                    className={`${styles.voteButton} ${idea.userVote === 'up' ? styles.voted : ''}`}
                    onClick={() => handleVote(idea.ProjectId, 'up')}
                    title="Vote for this idea"
                  >
                    👍 {idea.voteCount}
                  </button>

                  <button
                    className={`${styles.voteButton} ${idea.userVote === 'down' ? styles.voted : ''}`}
                    onClick={() => handleVote(idea.ProjectId, 'down')}
                    title="Vote against this idea"
                  >
                    👎
                  </button>

                  <span className={styles.comments}>
                    💬 {idea.commentCount}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
